import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-[var(--radius-xl)] border border-[var(--border)] bg-[var(--card)] p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--error)]/10">
            <AlertTriangle className="h-6 w-6 text-[var(--error)]" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-[var(--foreground)]">Something went wrong</h3>
            <p className="text-xs text-[var(--muted-foreground)]">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={this.handleReset}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Try again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

/** Page-level error boundary with full-page fallback */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={
        <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-[var(--background)] p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--error)]/10">
            <AlertTriangle className="h-8 w-8 text-[var(--error)]" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-[var(--foreground)]">Application Error</h2>
            <p className="max-w-md text-sm text-[var(--muted-foreground)]">
              The application encountered an unexpected error. Please refresh the page or try again.
            </p>
          </div>
          <Button onClick={() => window.location.reload()} className="rounded-[var(--radius-lg)]">
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload Page
          </Button>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
